import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, ProgressBar, Table, Badge } from 'react-bootstrap';
import { FaUpload, FaCheckCircle, FaTimesCircle, FaFilePdf } from 'react-icons/fa';
import '../../../styles/carga-recibos.css';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4004' 
  : '';

const CargaRecibos = () => {
  const [mes, setMes] = useState('');
  const [secuencia, setSecuencia] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultados, setResultados] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validar que sean PDFs
    const pdfFiles = files.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    
    if (pdfFiles.length !== files.length) {
      setError('Solo se permiten archivos PDF');
      return;
    }

    // Validar nombres (formato: YYYYMMSOXXX donde XXX = legajo)
    // Ejemplo: 2025121O9054 = año 2025, mes 12, sec 1, Original, legajo 9054
    const archivosConLegajo = pdfFiles.map(file => {
      const nombreSinExt = file.name.replace('.pdf', '');
      
      // Formato esperado: YYYYMMSOXXX (mínimo 10 caracteres)
      // YYYY = año (4 dígitos), MM = mes (2 dígitos), S = secuencia (1 dígito)
      // O = letra O de Original, XXX+ = legajo (resto de caracteres)
      const regex = /^(\d{4})(\d{2})(\d{1})O(\d+)$/;
      const match = nombreSinExt.match(regex);
      
      let legajo = null;
      let valido = false;
      let errorMsg = null;
      
      if (match) {
        const [, year, month, seq, leg] = match;
        legajo = parseInt(leg);
        valido = true;
        
        // Validación opcional: verificar que coincida con mes/secuencia ingresados
        if (mes && secuencia) {
          const [inputYear, inputMonth] = mes.split('-');
          if (month !== inputMonth || seq !== secuencia) {
            valido = false;
            errorMsg = `No coincide: esperado ${inputYear}${inputMonth}${secuencia}O${leg}`;
          }
        }
      } else {
        errorMsg = 'Formato inválido. Debe ser: YYYYMMSOXXX (ej: 2025121O9054)';
      }
      
      return {
        file,
        nombre: file.name,
        legajo: legajo,
        valido: valido,
        errorMsg: errorMsg,
        size: (file.size / 1024).toFixed(2) + ' KB'
      };
    });

    setArchivos(archivosConLegajo);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileChange({ target: { files } });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!mes || !secuencia) {
      setError('Debe ingresar Mes y Secuencia');
      return;
    }

    if (archivos.length === 0) {
      setError('Debe seleccionar al menos un archivo');
      return;
    }

    const archivosInvalidos = archivos.filter(a => !a.valido);
    if (archivosInvalidos.length > 0) {
      const ejemplos = archivosInvalidos.slice(0, 3).map(a => a.nombre).join(', ');
      setError(`${archivosInvalidos.length} archivos con formato inválido. Ejemplo: ${ejemplos}. Formato esperado: YYYYMMSOXXX (2025121O9054)`);
      return;
    }

    try {
      setUploading(true);
      setProgress(0);
      setError(null);

      // Convertir mes de formato YYYY-MM a MM/YYYY
      const [year, month] = mes.split('-');
      const mesFormateado = `${month}/${year}`;

      const formData = new FormData();
      formData.append('mes', mesFormateado);
      formData.append('secuencia', secuencia);
      
      archivos.forEach(({ file }) => {
        formData.append('files', file);
      });

      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/recibos/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al subir recibos');
      }

      const result = await response.json();
      setResultados(result.data);
      setProgress(100);
      
      // Limpiar después de éxito
      setTimeout(() => {
        setArchivos([]);
        setResultados(null);
        setProgress(0);
      }, 5000);

    } catch (err) {
      console.error('Error uploading:', err);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeArchivo = (index) => {
    setArchivos(archivos.filter((_, i) => i !== index));
  };

  const archivosValidos = archivos.filter(a => a.valido).length;
  const archivosInvalidos = archivos.filter(a => !a.valido).length;

  return (
    <Container className="carga-recibos-container py-4">
      <Row className="mb-4">
        <Col>
          <h2><FaUpload className="me-2" />Carga Masiva de Recibos de Sueldo</h2>
        </Col>
      </Row>

      <Alert variant="info" className="mb-3">
        <strong>📋 Formato de nombres de archivo:</strong> <code>YYYYMMSOXXX.pdf</code>
        <ul className="mb-0 mt-2" style={{fontSize: '0.9rem'}}>
          <li><strong>YYYY</strong> = Año (4 dígitos)</li>
          <li><strong>MM</strong> = Mes (2 dígitos, 01-12)</li>
          <li><strong>S</strong> = Secuencia (1 dígito)</li>
          <li><strong>O</strong> = Letra O mayúscula de "Original"</li>
          <li><strong>XXX</strong> = Legajo del empleado (variable)</li>
        </ul>
        <div className="mt-2" style={{fontSize: '0.95rem'}}>
          <strong>✅ Ejemplo correcto:</strong> <code>2025121O9054.pdf</code> 
          <small className="text-muted ms-2">(Año 2025, Mes 12, Secuencia 1, Original, Legajo 9054)</small>
        </div>
      </Alert>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {resultados && (
        <Alert variant="success">
          <FaCheckCircle className="me-2" />
          Recibos cargados exitosamente:
          <ul className="mb-0 mt-2">
            <li><strong>{resultados.exitosos}</strong> archivos subidos</li>
            {resultados.errores > 0 && <li className="text-danger"><strong>{resultados.errores}</strong> errores</li>}
          </ul>
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Mes (Período) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="month"
                    value={mes}
                    onChange={(e) => setMes(e.target.value)}
                    required
                    disabled={uploading}
                    placeholder="YYYY-MM"
                  />
                  <Form.Text className="text-muted">
                    Formato: YYYY-MM (ej: 2024-01)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Secuencia <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    value={secuencia}
                    onChange={(e) => setSecuencia(e.target.value)}
                    required
                    min="1"
                    disabled={uploading}
                    placeholder="1, 2, 3..."
                  />
                  <Form.Text className="text-muted">
                    Número de secuencia del período
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Archivos PDF <span className="text-danger">*</span></Form.Label>
              <div 
                className="dropzone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  type="file"
                  id="file-input"
                  multiple
                  accept=".pdf"
                  onChange={handleFileChange}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-input" className="dropzone-label">
                  <FaUpload size={48} className="mb-3 text-primary" />
                  <h5>Arrastre archivos aquí o haga clic para seleccionar</h5>
                  <p className="text-muted">Archivos PDF nombrados con el legajo (máximo 1000 archivos, 10MB cada uno)</p>
                </label>
              </div>
            </Form.Group>

            {archivos.length > 0 && (
              <>
                <div className="mb-3">
                  <Badge bg="success" className="me-2">{archivosValidos} válidos</Badge>
                  {archivosInvalidos > 0 && <Badge bg="danger">{archivosInvalidos} inválidos</Badge>}
                </div>

                <div className="archivos-list mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th width="50px">#</th>
                        <th>Archivo</th>
                        <th width="100px">Legajo</th>
                        <th width="100px">Tamaño</th>
                        <th width="80px">Estado</th>
                        <th width="80px">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivos.map((archivo, index) => (
                        <tr key={index} className={archivo.valido ? '' : 'table-danger'}>
                          <td>{index + 1}</td>
                          <td>
                            <FaFilePdf className="text-danger me-2" />
                            {archivo.nombre}
                          </td>
                          <td>{archivo.legajo || 'N/A'}</td>
                          <td>{archivo.size}</td>
                          <td>
                            {archivo.valido ? (
                              <span className="text-success">
                                <FaCheckCircle className="me-1" />
                                Válido
                              </span>
                            ) : (
                              <span className="text-danger" title={archivo.errorMsg}>
                                <FaTimesCircle className="me-1" />
                                {archivo.errorMsg || 'Inválido'}
                              </span>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeArchivo(index)}
                              disabled={uploading}
                            >
                              ✕
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}

            {uploading && (
              <ProgressBar 
                now={progress} 
                label={`${progress}%`} 
                animated 
                className="mb-3"
              />
            )}

            <div className="d-flex gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={uploading || archivos.length === 0 || archivosInvalidos > 0}
              >
                {uploading ? 'Subiendo...' : `Subir ${archivos.length} Recibo(s)`}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setArchivos([]);
                  setMes('');
                  setSecuencia('');
                  setError(null);
                  setResultados(null);
                }}
                disabled={uploading}
              >
                Limpiar
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h5 className="mb-0">💡 Instrucciones</h5>
        </Card.Header>
        <Card.Body>
          <ol>
            <li><strong>Mes y Secuencia:</strong> Ingrese el período (YYYY-MM) y número de secuencia</li>
            <li><strong>Nombres de archivos:</strong> Los PDFs deben nombrarse con el legajo del empleado (ej: 123.pdf, 7393.pdf)</li>
            <li><strong>Selección:</strong> Arrastre los archivos o haga clic en la zona de carga</li>
            <li><strong>Validación:</strong> El sistema valida automáticamente que los nombres sean legajos válidos</li>
            <li><strong>Carga:</strong> Los recibos se guardan en <code>Recibos/{'{Mes}'}/{'{Secuencia}'}/{'{Legajo}'}.pdf</code></li>
          </ol>
          <Alert variant="info" className="mb-0 mt-3">
            <strong>Nota:</strong> Este sistema reemplaza la carga manual por FTP. Los empleados verán los recibos automáticamente una vez cargados.
          </Alert>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CargaRecibos;
